// v3.15-952-CLIENTS-TIMEZONE: utilities for deriving + formatting a
// client's local timezone, plus computing a "Best Time to Call" hint.
// All logic is pure and client-side so filtering is instant.

// US state → IANA timezone. Multi-zone states default to the most populous
// zone (we keep this conservative — a client can override via client.timezone
// if it differs from the state default).
const STATE_TZ_MAP = {
  AL: 'America/Chicago',     AK: 'America/Anchorage',
  AZ: 'America/Phoenix',     AR: 'America/Chicago',
  CA: 'America/Los_Angeles', CO: 'America/Denver',
  CT: 'America/New_York',    DE: 'America/New_York',
  DC: 'America/New_York',    FL: 'America/New_York',
  GA: 'America/New_York',    HI: 'Pacific/Honolulu',
  ID: 'America/Boise',       IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis', IA: 'America/Chicago',
  KS: 'America/Chicago',     KY: 'America/New_York',
  LA: 'America/Chicago',     ME: 'America/New_York',
  MD: 'America/New_York',    MA: 'America/New_York',
  MI: 'America/Detroit',     MN: 'America/Chicago',
  MS: 'America/Chicago',     MO: 'America/Chicago',
  MT: 'America/Denver',      NE: 'America/Chicago',
  NV: 'America/Los_Angeles', NH: 'America/New_York',
  NJ: 'America/New_York',    NM: 'America/Denver',
  NY: 'America/New_York',    NC: 'America/New_York',
  ND: 'America/Chicago',     OH: 'America/New_York',
  OK: 'America/Chicago',     OR: 'America/Los_Angeles',
  PA: 'America/New_York',    RI: 'America/New_York',
  SC: 'America/New_York',    SD: 'America/Chicago',
  TN: 'America/Chicago',     TX: 'America/Chicago',
  UT: 'America/Denver',      VT: 'America/New_York',
  VA: 'America/New_York',    WA: 'America/Los_Angeles',
  WV: 'America/New_York',    WI: 'America/Chicago',
  WY: 'America/Denver',      PR: 'America/Puerto_Rico',
};

const TZ_BUCKETS = {
  EST: ['America/New_York', 'America/Detroit', 'America/Indiana/Indianapolis', 'America/Puerto_Rico'],
  CST: ['America/Chicago'],
  MST: ['America/Denver', 'America/Boise', 'America/Phoenix'],
  PST: ['America/Los_Angeles'],
};

const _normalizeStateRaw = (raw) => {
  if (!raw) return '';
  const v = String(raw).trim().toUpperCase();
  if (STATE_TZ_MAP[v]) return v;
  // Allow full-name fallback for the most common ones.
  const FULL_TO_ABBR = {
    ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA',
    COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE', FLORIDA: 'FL', GEORGIA: 'GA',
    HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA',
    KANSAS: 'KS', KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD',
    MASSACHUSETTS: 'MA', MICHIGAN: 'MI', MINNESOTA: 'MN', MISSISSIPPI: 'MS',
    MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE', NEVADA: 'NV',
    'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ', 'NEW MEXICO': 'NM', 'NEW YORK': 'NY',
    'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK',
    OREGON: 'OR', PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT',
    VIRGINIA: 'VA', WASHINGTON: 'WA', 'WEST VIRGINIA': 'WV', WISCONSIN: 'WI',
    WYOMING: 'WY', 'PUERTO RICO': 'PR',
  };
  return FULL_TO_ABBR[v] || '';
};

// Resolve a client's IANA TZ. Priority: explicit client.timezone > state map.
export const deriveClientTimezone = (client) => {
  if (!client) return '';
  const explicit = (client.timezone || '').trim();
  if (explicit) return explicit;
  const state = _normalizeStateRaw(client.state);
  return STATE_TZ_MAP[state] || '';
};

// Map an IANA TZ to one of EST/CST/MST/PST (or '' for outside-US).
export const tzBucketFor = (ianaTz) => {
  if (!ianaTz) return '';
  for (const [bucket, list] of Object.entries(TZ_BUCKETS)) {
    if (list.includes(ianaTz)) return bucket;
  }
  return '';
};

// Short TZ abbreviation as the browser would render (handles DST, EDT/EST).
export const tzAbbrFor = (ianaTz, ref = new Date()) => {
  if (!ianaTz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTz, timeZoneName: 'short',
    }).formatToParts(ref);
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part ? part.value : '';
  } catch {
    return '';
  }
};

// Convert "now" to the rep-local hour in `ianaTz`. Returns 0-23 or NaN.
const _hourInTz = (ianaTz, ref = new Date()) => {
  if (!ianaTz) return NaN;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTz, hour: 'numeric', hour12: false,
    }).formatToParts(ref);
    const h = parts.find((p) => p.type === 'hour');
    return h ? parseInt(h.value, 10) : NaN;
  } catch {
    return NaN;
  }
};

// Default best-call windows (client-local): 9-12 and 16-19. Returns the
// next applicable window's start hour (e.g. 9 if before 9, 16 if past noon
// and before 16, otherwise 9 of next day).
const BEST_WINDOWS = [
  [9, 12],
  [16, 19],
];

// Returns:
//   { label: "10:00 AM EST", abbr: "EST", live: true|false }
// `live` is true when the current time in the client's TZ is INSIDE one of
// the BEST_WINDOWS — used to color the dot green vs gray.
export const bestTimeToCall = (client) => {
  const tz = deriveClientTimezone(client);
  if (!tz) return null;
  const abbr = tzAbbrFor(tz);
  const now = new Date();
  const hourNow = _hourInTz(tz, now);
  let live = false;
  let nextStart = null;
  for (const [s, e] of BEST_WINDOWS) {
    if (hourNow >= s && hourNow < e) { live = true; nextStart = s; break; }
  }
  if (!live) {
    for (const [s] of BEST_WINDOWS) {
      if (hourNow < s) { nextStart = s; break; }
    }
    if (nextStart === null) nextStart = BEST_WINDOWS[0][0]; // wraps to tomorrow 9am
  }
  const ampm = nextStart >= 12 ? 'PM' : 'AM';
  const display = nextStart > 12 ? nextStart - 12 : (nextStart === 0 ? 12 : nextStart);
  return {
    label: `${display}:00 ${ampm}${abbr ? ' ' + abbr : ''}`,
    abbr:  abbr || tzBucketFor(tz),
    live,
  };
};

export const TZ_FILTER_OPTIONS = [
  { value: 'all', label: 'All Time Zones' },
  { value: 'EST', label: 'EST (Eastern)' },
  { value: 'CST', label: 'CST (Central)' },
  { value: 'MST', label: 'MST (Mountain)' },
  { value: 'PST', label: 'PST (Pacific)' },
];
